import { useState, useEffect, createContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import ArchivePage from "@/pages/ArchivePage";
import AccountPage from "@/pages/AccountPage";
import AdminFeedbackPage from "@/pages/AdminFeedbackPage";
import AdminSubscribersPage from "@/pages/AdminSubscribersPage";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to always send session token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

export const AuthContext = createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for session_id in URL fragment (after OAuth redirect)
      const fragment = window.location.hash;
      if (fragment.includes('session_id=')) {
        const sessionId = fragment.split('session_id=')[1].split('&')[0];
        await processSessionId(sessionId);
        // Clean URL
        window.history.replaceState(null, '', window.location.pathname);
        return;
      }

      // Check existing session from localStorage
      const token = localStorage.getItem('session_token');
      if (token) {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        await fetchSubscriptionStatus();
      }
    } catch (error) {
      console.log('Not authenticated');
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId }
      );
      setUser(response.data);
      // Store session token in localStorage
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      await fetchSubscriptionStatus();
    } catch (error) {
      console.error('Failed to process session:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      setUser(null);
      setSubscriptionStatus(null);
      localStorage.removeItem('session_token');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_insurecloud-news/artifacts/g99eghuw_Owl%20Ai%20animated%20Gif.gif" 
            alt="Loading"
            className="h-12 w-12"
          />
          <span className="text-lg text-slate-600">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, subscriptionStatus, logout, refreshSubscription: fetchSubscriptionStatus }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/" element={user ? <HomePage /> : <Navigate to="/login" />} />
            <Route path="/archive" element={user ? <ArchivePage /> : <Navigate to="/login" />} />
            <Route path="/account" element={user ? <AccountPage /> : <Navigate to="/login" />} />
            <Route path="/subscription/success" element={user ? <Navigate to="/account?session_id=" /> : <Navigate to="/login" />} />
            <Route path="/admin/feedback" element={user ? <AdminFeedbackPage /> : <Navigate to="/login" />} />
            <Route path="/admin/subscribers" element={user ? <AdminSubscribersPage /> : <Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;